import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { DEFAULT_INDUSTRY_TYPE, getIndustryType } from '@/firebase/organizationService';
import { Organization } from '@/firebase/organizationService';

// Industry-specific default templates
// This will be populated with templates for each industry as they are provided
export interface IndustryTemplateData {
  industry: string;
  templates: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>>;
}

// Default templates for "Accommodation and Food Services" industry
const ACCOMMODATION_FOOD_SERVICES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Annual Fire Inspection",
    "category": "Life Safety",
    "frequency": "P1Y",
    "lead_times_days": [60, 30, 7],
    "owner_role": "Site Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Fire Marshal Certificate" },
      { "type": "photo", "label": "Panel Photos" }
    ],
    "pass_fail_criteria": "All devices pass functional test; certificate received and filed.",
    "jurisdiction": "County/Fire Marshal",
    "jurisdiction_notes": "Salt Lake County Fire Marshal requires on-site posting of certificate for 12 months.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Extinguisher Monthly Check",
    "category": "Life Safety",
    "frequency": "P1M",
    "lead_times_days": [7, 2],
    "owner_role": "Engineering Lead",
    "evidence_types": [{ "type": "photo", "label": "Tagged Units" }],
    "pass_fail_criteria": "All extinguishers accessible, tagged, gauge in green; log updated.",
    "jurisdiction": "OSHA/Fire Code",
    "jurisdiction_notes": "Maintain 12 months of logs; OSHA 29 CFR 1910.157.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Lighting Test",
    "category": "Life Safety",
    "frequency": "P1M",
    "lead_times_days": [7, 2],
    "owner_role": "Engineering Lead",
    "evidence_types": [{ "type": "photo", "label": "Test Log Photos" }],
    "pass_fail_criteria": "All fixtures illuminate for test duration; defects corrected.",
    "jurisdiction": "NFPA/Local AHJ",
    "jurisdiction_notes": "NFPA 101 requires 90-minute annual test; schedule separately.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hood & Duct Cleaning",
    "category": "Life Safety",
    "frequency": "P6M",
    "lead_times_days": [30, 7],
    "owner_role": "Kitchen Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Cleaning Certificate" },
      { "type": "photo", "label": "Before/After" }
    ],
    "pass_fail_criteria": "No grease accumulation; baffle filters reinstalled; certificate on file.",
    "jurisdiction": "Health Dept./Insurer",
    "jurisdiction_notes": "Frequency may increase with heavy/fry volume; verify with insurer/AHJ.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Backflow Testing",
    "category": "Life Safety",
    "frequency": "P1Y",
    "lead_times_days": [45, 15, 7],
    "owner_role": "Maintenance Supervisor",
    "evidence_types": [{ "type": "pdf", "label": "Certified Test Report" }],
    "pass_fail_criteria": "Passed differential pressure test; report submitted if required.",
    "jurisdiction": "Water Authority",
    "jurisdiction_notes": "Annual certified tester report typically required by water purveyor.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Sprinkler 5-Year Internal Inspection",
    "category": "Life Safety",
    "frequency": "P5Y",
    "lead_times_days": [90, 60, 30],
    "owner_role": "Engineering Lead",
    "evidence_types": [{ "type": "pdf", "label": "Internal Inspection Report" }],
    "pass_fail_criteria": "Pipes free of corrosion/obstruction per NFPA 25; deficiencies corrected.",
    "jurisdiction": "NFPA/Local AHJ",
    "jurisdiction_notes": "NFPA 25 standard item; confirm local adoption with AHJ.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Elevator Permit Renewal",
    "category": "Building & Facilities",
    "frequency": "P1Y",
    "lead_times_days": [90, 60, 30],
    "owner_role": "Facilities Manager",
    "evidence_types": [{ "type": "pdf", "label": "Renewed Permit" }],
    "pass_fail_criteria": "Current permit posted; inspection passed; renewal filed.",
    "jurisdiction": "State/Local Building Dept.",
    "jurisdiction_notes": "Annual inspection typically required before permit renewal.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Boiler Inspection",
    "category": "Building & Facilities",
    "frequency": "P1Y",
    "lead_times_days": [45, 15, 7],
    "owner_role": "Engineering Lead",
    "evidence_types": [{ "type": "pdf", "label": "Inspection Report" }],
    "pass_fail_criteria": "Safety valves operational; no leaks; certificate or report on file.",
    "jurisdiction": "State/Insurer",
    "jurisdiction_notes": "State inspector or insurance carrier inspection may be required.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "ADA Door Operator Test",
    "category": "Building & Facilities",
    "frequency": "P3M",
    "lead_times_days": [14, 7],
    "owner_role": "Engineering Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Maintenance Log" },
      { "type": "photo", "label": "Door Plate Labels" }
    ],
    "pass_fail_criteria": "Meets opening timing/force and sensor safety; labels present.",
    "jurisdiction": "ANSI/ADA",
    "jurisdiction_notes": "ANSI/BHMA A156.10/19; ensure safety stickers are visible.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Generator Load Test",
    "category": "Building & Facilities",
    "frequency": "P1M",
    "lead_times_days": [7, 2],
    "owner_role": "Engineering Lead",
    "evidence_types": [{ "type": "pdf", "label": "Generator Log" }],
    "pass_fail_criteria": "Starts automatically and runs under load; log completed.",
    "jurisdiction": "NFPA 110",
    "jurisdiction_notes": "Maintain monthly exercise logs; service per manufacturer.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Food Safety Inspection",
    "category": "Food Safety",
    "frequency": "P6M",
    "lead_times_days": [30, 14, 7],
    "owner_role": "Kitchen Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Health Inspection Report" },
      { "type": "photo", "label": "Corrective Actions" }
    ],
    "pass_fail_criteria": "Inspection passed with no critical violations; corrective actions documented.",
    "jurisdiction": "Local Health Department",
    "jurisdiction_notes": "Frequency varies by jurisdiction; typically semi-annual or annual.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Pest Control Service",
    "category": "Food Safety",
    "frequency": "P1M",
    "lead_times_days": [7, 2],
    "owner_role": "Kitchen Manager",
    "evidence_types": [{ "type": "pdf", "label": "Service Report" }],
    "pass_fail_criteria": "Monthly service completed; no active pest issues; traps monitored.",
    "jurisdiction": "Health Department",
    "jurisdiction_notes": "Regular pest control service required; maintain service logs.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Grease Trap Maintenance",
    "category": "Food Safety",
    "frequency": "P3M",
    "lead_times_days": [30, 14, 7],
    "owner_role": "Kitchen Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Service Certificate" },
      { "type": "photo", "label": "Before/After" }
    ],
    "pass_fail_criteria": "Trap cleaned and serviced; no overflow or backup issues.",
    "jurisdiction": "Local Health/Water Authority",
    "jurisdiction_notes": "Frequency may vary based on volume and local regulations.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Business License Renewal",
    "category": "Licensing & Administrative",
    "frequency": "P1Y",
    "lead_times_days": [60, 30, 7],
    "owner_role": "Site Manager",
    "evidence_types": [{ "type": "pdf", "label": "License" }],
    "pass_fail_criteria": "License renewed and posted; receipt on file.",
    "jurisdiction": "City",
    "jurisdiction_notes": "Renew via city portal; posting may be required.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Health Department Permit",
    "category": "Licensing & Administrative",
    "frequency": "P1Y",
    "lead_times_days": [60, 30, 7],
    "owner_role": "Site Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Permit" },
      { "type": "photo", "label": "Posting" }
    ],
    "pass_fail_criteria": "Permit active and visibly posted.",
    "jurisdiction": "County Health Dept.",
    "jurisdiction_notes": "Salt Lake County Health Department oversees most food permits.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Liquor License Renewal",
    "category": "Licensing & Administrative",
    "frequency": "P1Y",
    "lead_times_days": [90, 60, 30],
    "owner_role": "Owner/Admin",
    "evidence_types": [{ "type": "pdf", "label": "License/Receipt" }],
    "pass_fail_criteria": "License renewed before expiration; documentation on file.",
    "jurisdiction": "State ABC",
    "jurisdiction_notes": "Utah DABS; start early to avoid lapse.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Workers Comp Poster Update",
    "category": "Licensing & Administrative",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR Manager",
    "evidence_types": [{ "type": "photo", "label": "Posted Poster" }],
    "pass_fail_criteria": "Current state poster displayed in common area.",
    "jurisdiction": "State Labor",
    "jurisdiction_notes": "Utah Labor Commission posting required; update when content changes.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Occupational Safety Posters",
    "category": "Licensing & Administrative",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR Manager",
    "evidence_types": [{ "type": "photo", "label": "Posting Area" }],
    "pass_fail_criteria": "OSHA-required posters up-to-date and visible.",
    "jurisdiction": "Federal/OSHA",
    "jurisdiction_notes": "OSHA 3165; some posters must be updated when rules change.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Waste Hauler Permit",
    "category": "Environmental & Utilities",
    "frequency": "P1Y",
    "lead_times_days": [60, 30, 7],
    "owner_role": "Site Manager",
    "evidence_types": [{ "type": "pdf", "label": "Permit" }],
    "pass_fail_criteria": "Permit active; vendor compliant with pickups.",
    "jurisdiction": "City/County",
    "jurisdiction_notes": "Renew through local waste authority; keep copy on file.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Recycling Audit",
    "category": "Environmental & Utilities",
    "frequency": "P6M",
    "lead_times_days": [30],
    "owner_role": "Facilities Manager",
    "evidence_types": [{ "type": "photo", "label": "Bins & Signage" }],
    "pass_fail_criteria": "Correct bins present; signage readable; contamination minimized.",
    "jurisdiction": "Corporate/Voluntary",
    "jurisdiction_notes": "Often part of sustainability programs; not always mandated.",
    "risk_level": "Low",
    "mark_due_in_days": 30
  },
  {
    "name": "Workers Comp Audit",
    "category": "Insurance & Finance",
    "frequency": "P1Y",
    "lead_times_days": [45, 15, 7],
    "owner_role": "Owner/Admin",
    "evidence_types": [
      { "type": "pdf", "label": "Audit Letter" },
      { "type": "pdf", "label": "Proof of Payment" }
    ],
    "pass_fail_criteria": "Audit completed; variances reconciled; payment posted.",
    "jurisdiction": "Insurer/State",
    "jurisdiction_notes": "Retain records for 3–5 years for audits.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "CPR / First Aid Certification",
    "category": "Training & HR",
    "frequency": "P2Y",
    "lead_times_days": [60, 30],
    "owner_role": "HR Manager",
    "evidence_types": [{ "type": "pdf", "label": "Certificates" }],
    "pass_fail_criteria": "At least one staff member per shift certified.",
    "jurisdiction": "Best Practice/Insurer",
    "jurisdiction_notes": "Often required by insurer or brand standards.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Harassment Prevention Training",
    "category": "Training & HR",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR Manager",
    "evidence_types": [{ "type": "pdf", "label": "Completion Log" }],
    "pass_fail_criteria": "100% of staff completed by deadline.",
    "jurisdiction": "State/Company Policy",
    "jurisdiction_notes": "Utah mandates annual training for many employers; confirm scope.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Food Handler Permit",
    "category": "Training & HR",
    "frequency": "P3Y",
    "lead_times_days": [90, 30, 7],
    "owner_role": "Kitchen Manager",
    "evidence_types": [{ "type": "pdf", "label": "Cards" }],
    "pass_fail_criteria": "All food handlers hold valid, non-expired permits.",
    "jurisdiction": "County Health Dept.",
    "jurisdiction_notes": "Salt Lake County Health Dept. issues/validates permits.",
    "risk_level": "High"
  }
];

// Default templates for "Construction" industry
const CONSTRUCTION_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Site Safety Inspection (Daily)",
    "category": "Safety",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Site Supervisor / Safety Officer",
    "evidence_types": [
      { "type": "photo", "label": "Inspection Photos" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "No imminent hazards; PPE in use; access and egress clear",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Follow OSHA 29 CFR 1926; document corrective actions",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Toolbox Talk / Safety Briefing (Per Shift or Task)",
    "category": "Training",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Foreman / Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Completed Form" },
      { "type": "pdf", "label": "Signed Document" },
      { "type": "photo", "label": "Training Photos" }
    ],
    "pass_fail_criteria": "Attendance recorded; key hazards and controls communicated",
    "jurisdiction": "Company Policy",
    "jurisdiction_notes": "Keep roster and topics for 3 years per company policy or local rules",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Scaffold Inspection (Weekly & After Alteration)",
    "category": "Equipment",
    "frequency": "P1W",
    "lead_times_days": [2],
    "owner_role": "Competent Person / Scaffold Supervisor",
    "evidence_types": [
      { "type": "photo", "label": "Inspection Photos" },
      { "type": "pdf", "label": "Inspection Form" },
      { "type": "photo", "label": "Inspection Tag" }
    ],
    "pass_fail_criteria": "Scaffold components secure; load limits posted; tag present",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA scaffold standards; tag scaffolds after inspection",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Fall Protection Equipment Inspection (Monthly)",
    "category": "Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "EHS / Safety Manager",
    "evidence_types": [
      { "type": "photo", "label": "Equipment Photos" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "Harnesses, lanyards, anchors within serviceable limits; no visible damage",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Inspect per manufacturer and OSHA guidance; retire if damaged",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Heavy Equipment Pre-Op Inspection (Daily)",
    "category": "Maintenance",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Equipment Operator / Fleet Manager",
    "evidence_types": [
      { "type": "photo", "label": "Equipment Photos" },
      { "type": "pdf", "label": "Inspection Form" },
      { "type": "photo", "label": "Measurement Record" }
    ],
    "pass_fail_criteria": "Critical systems functional; no fluid leaks; safety devices operating",
    "jurisdiction": "Manufacturer Standards",
    "jurisdiction_notes": "Keep operator logs; follow manufacturer pre-op checklist",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Crane & Lifting Gear Inspection (Monthly / Before Lift)",
    "category": "Equipment",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Lift Supervisor / Competent Person",
    "evidence_types": [
      { "type": "photo", "label": "Inspection Photos" },
      { "type": "pdf", "label": "Certificate" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "Crane inspected; wire rope and slings within tolerance; load test records present if required",
    "jurisdiction": "ASME",
    "jurisdiction_notes": "Follow ASME B30 standards and local requirements",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Permits & Licensing Check (Weekly)",
    "category": "Administrative",
    "frequency": "P1W",
    "lead_times_days": [7],
    "owner_role": "Project Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Permit Documents" },
      { "type": "photo", "label": "Permit Photos" }
    ],
    "pass_fail_criteria": "Required permits are on-site and current; insurance certificates on file",
    "jurisdiction": "Local Building Department",
    "jurisdiction_notes": "Confirm local building department and special permits (road closures, hot work)",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hot Work Permit & Controls (On Event)",
    "category": "Fire Safety",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Hot Work Supervisor / Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Permit Form" },
      { "type": "photo", "label": "Work Area Photos" }
    ],
    "pass_fail_criteria": "Permit issued; fire watch assigned; combustible materials moved/covered",
    "jurisdiction": "NFPA",
    "jurisdiction_notes": "Follow NFPA 51B and site fire plan",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hazardous Material Storage Inspection (Weekly)",
    "category": "Environmental",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "EHS / Site Supervisor",
    "evidence_types": [
      { "type": "photo", "label": "Storage Photos" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "Containers labeled and sealed; secondary containment in place; MSDS accessible",
    "jurisdiction": "EPA",
    "jurisdiction_notes": "Comply with EPA and state hazardous material handling rules",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Stormwater / Erosion Control Inspection (After Rain Events)",
    "category": "Environmental",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Environmental Manager / Site Supervisor",
    "evidence_types": [
      { "type": "photo", "label": "Control Measures Photos" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "Silt fences and BMPs functional; no unauthorized discharge",
    "jurisdiction": "NPDES",
    "jurisdiction_notes": "Document per NPDES permit conditions if applicable",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Confined Space Entry Permit & Verification (Per Entry)",
    "category": "Safety",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Entry Supervisor / Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Entry Permit Form" },
      { "type": "pdf", "label": "Gas Reading Report" },
      { "type": "photo", "label": "Entry Photos" }
    ],
    "pass_fail_criteria": "Atmosphere tested and safe; attendants assigned; rescue plan in place",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Follow OSHA 29 CFR 1910.146; record gas readings",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Lockout/Tagout Procedure Verification (Monthly)",
    "category": "Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Electrical Supervisor / EHS",
    "evidence_types": [
      { "type": "photo", "label": "LOTO Devices Photos" },
      { "type": "pdf", "label": "Verification Form" }
    ],
    "pass_fail_criteria": "LOTO procedures followed; authorized personnel trained; devices labeled",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Follow OSHA LOTO standard 29 CFR 1910.147",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Welding Fume & Ventilation Check (Weekly)",
    "category": "Health",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "EHS / Welding Supervisor",
    "evidence_types": [
      { "type": "photo", "label": "Measurement Record" },
      { "type": "photo", "label": "Ventilation Photos" },
      { "type": "pdf", "label": "Inspection Form" }
    ],
    "pass_fail_criteria": "Local exhaust in place and functional; respirator program followed",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Follow OSHA welding & cutting standards and respiratory protection standard",
    "risk_level": "Medium"
  }
];

// Default templates for "Healthcare and Social Assistance" industry
const HEALTHCARE_SOCIAL_ASSISTANCE_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "HIPAA Privacy and Security Compliance Audit (Annual)",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [45],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Policy Document" },
      { "type": "pdf", "label": "Training Log" },
      { "type": "pdf", "label": "Audit Report" }
    ],
    "pass_fail_criteria": "All required safeguards (administrative, physical, technical) documented and compliant; no high-risk gaps",
    "jurisdiction": "HIPAA/HITECH",
    "jurisdiction_notes": "Required under HIPAA and HITECH regulations; retain reports for 6 years",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Infection Control and Prevention Audit (Quarterly)",
    "category": "Clinical Safety",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Infection Control Nurse / Quality Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Report" },
      { "type": "photo", "label": "Inspection Photos" },
      { "type": "pdf", "label": "Training Record" }
    ],
    "pass_fail_criteria": "All high-risk areas meet sanitation standards; no expired PPE or disinfectants",
    "jurisdiction": "CDC/Joint Commission",
    "jurisdiction_notes": "Comply with CDC and Joint Commission standards",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Medical Equipment Preventive Maintenance (Monthly)",
    "category": "Equipment Safety",
    "frequency": "P1M",
    "lead_times_days": [10],
    "owner_role": "Biomedical Engineer / Maintenance Technician",
    "evidence_types": [
      { "type": "pdf", "label": "Maintenance Log" },
      { "type": "pdf", "label": "Service Certificate" }
    ],
    "pass_fail_criteria": "All equipment passes calibration and function checks",
    "jurisdiction": "CMS/Joint Commission",
    "jurisdiction_notes": "CMS and Joint Commission require maintenance logs for critical devices",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "OSHA Safety and Bloodborne Pathogens Training (Annual)",
    "category": "Employee Safety",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR / Safety Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Attendance Record" },
      { "type": "pdf", "label": "Training Certificate" }
    ],
    "pass_fail_criteria": "100% of staff complete annual training; records stored securely",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA 29 CFR 1910.1030 applies to all healthcare staff",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Fire and Emergency Evacuation Drill (Biannual)",
    "category": "Life Safety",
    "frequency": "P6M",
    "lead_times_days": [14],
    "owner_role": "Facilities / Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Log" },
      { "type": "photo", "label": "Drill Photos" },
      { "type": "pdf", "label": "Attendance Record" }
    ],
    "pass_fail_criteria": "All staff participate; evacuation within acceptable timeframe; drill report completed",
    "jurisdiction": "NFPA",
    "jurisdiction_notes": "NFPA 101 Life Safety Code applies; maintain logs for 3 years",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Patient Rights and Confidentiality Policy Review (Annual)",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Policy Document" },
      { "type": "pdf", "label": "Meeting Minutes" }
    ],
    "pass_fail_criteria": "Policies reviewed, approved, and accessible to staff and patients",
    "jurisdiction": "CMS/Joint Commission",
    "jurisdiction_notes": "Required by CMS and Joint Commission for all care facilities",
    "risk_level": "Medium"
  }
];

// Default templates for "Manufacturing" industry
const MANUFACTURING_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Safety & Hazard Control Inspection (Monthly)",
    "category": "Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Safety Officer / Plant Manager",
    "evidence_types": [
      { "type": "photo", "label": "Inspection Photos" },
      { "type": "pdf", "label": "Inspection Checklist" },
      { "type": "pdf", "label": "Incident Log" }
    ],
    "pass_fail_criteria": "No unmitigated critical hazards; guards & PPE compliance at required level",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Follow OSHA 29 CFR 1910 General Industry Standards",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Machine Pre-Operation Safety Check (Daily)",
    "category": "Safety",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Machine Operator / Line Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Safety Checklist" },
      { "type": "photo", "label": "Machine Photos" }
    ],
    "pass_fail_criteria": "Operator confirms machine safe before use",
    "jurisdiction": "Plant SOP",
    "jurisdiction_notes": "Plant-specific lockout/tagout SOPs must be followed",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Lockout / Tagout Procedure Compliance Audit (Monthly)",
    "category": "Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "EHS Manager",
    "evidence_types": [
      { "type": "photo", "label": "LOTO Devices Photos" },
      { "type": "pdf", "label": "Compliance Checklist" }
    ],
    "pass_fail_criteria": "All LOTO points labeled; authorized personnel trained; logs maintained",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA 1910.147 applies",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hazardous Chemical & Material Storage Inspection (Weekly)",
    "category": "Environmental Compliance",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "EHS Technician",
    "evidence_types": [
      { "type": "photo", "label": "Storage Area Photos" },
      { "type": "pdf", "label": "Inspection Checklist" }
    ],
    "pass_fail_criteria": "Containers labeled; SDS available; no leaks; secondary containment present",
    "jurisdiction": "EPA",
    "jurisdiction_notes": "EPA Hazard Communication Standard / SDS availability required",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Equipment Preventive Maintenance (Monthly)",
    "category": "Maintenance",
    "frequency": "P1M",
    "lead_times_days": [10],
    "owner_role": "Maintenance Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Service Log" },
      { "type": "photo", "label": "Equipment Photos" }
    ],
    "pass_fail_criteria": "Critical equipment serviced within schedule; no overdue PM tasks",
    "jurisdiction": "OEM Standards",
    "jurisdiction_notes": "Follow OEM recommended intervals",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Calibration Certificate Verification (Quarterly)",
    "category": "Quality Assurance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Quality Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Calibration Certificate" },
      { "type": "pdf", "label": "Calibration Log" }
    ],
    "pass_fail_criteria": "100% of gauges and measuring tools have valid calibration certificates",
    "jurisdiction": "ISO",
    "jurisdiction_notes": "ISO 9001 & ISO/IEC 17025 traceability expected",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Compressed Air System Audit (Monthly)",
    "category": "Operations",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Maintenance Team",
    "evidence_types": [
      { "type": "photo", "label": "Measurement Record" },
      { "type": "photo", "label": "System Photos" }
    ],
    "pass_fail_criteria": "Leaks minimized; pressure within tolerance; dryer functional",
    "jurisdiction": "Energy Efficiency",
    "jurisdiction_notes": "Energy efficiency & equipment reliability impact",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Fire Extinguisher & Suppression System Check (Monthly)",
    "category": "Life Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Facilities / Safety",
    "evidence_types": [
      { "type": "photo", "label": "Extinguisher Photos" },
      { "type": "photo", "label": "Inspection Tag" }
    ],
    "pass_fail_criteria": "Pressure gauge in range; pin intact; annual vendor inspection scheduled",
    "jurisdiction": "NFPA",
    "jurisdiction_notes": "NFPA 10 and local fire marshal requirements",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Forklift & Mobile Equipment Operator Certification (Annual)",
    "category": "Employee Training",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Safety / HR",
    "evidence_types": [
      { "type": "pdf", "label": "Training Record" },
      { "type": "pdf", "label": "Operator Certificate" }
    ],
    "pass_fail_criteria": "All operators certified and evaluated",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA 1910.178 requires annual evaluation",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Evacuation Drill (Annual)",
    "category": "Life Safety",
    "frequency": "P1Y",
    "lead_times_days": [14],
    "owner_role": "Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Log" },
      { "type": "photo", "label": "Drill Photos" },
      { "type": "pdf", "label": "Attendance Sheet" }
    ],
    "pass_fail_criteria": "Drill completed; evacuation time meets threshold",
    "jurisdiction": "NFPA",
    "jurisdiction_notes": "NFPA 101 Life Safety Code guidance",
    "risk_level": "Medium"
  }
];

// Default templates for "Retail and Wholesale Trade" industry
const RETAIL_WHOLESALE_TRADE_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Store Safety & Housekeeping Inspection",
    "category": "Safety",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Store Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Checklist" },
      { "type": "pdf", "label": "Corrective Action Log" }
    ],
    "pass_fail_criteria": "Routine inspection to ensure store floors, shelves, stockrooms, and exit paths are clean, clear, and safe.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "Maintain safe working environment per OSHA standards",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Point of Sale (POS) Reconciliation",
    "category": "Financial Compliance",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Accounting / Shift Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "POS Report" },
      { "type": "pdf", "label": "Cash Drawer Count Sheet" }
    ],
    "pass_fail_criteria": "Verify daily transaction records from registers match the accounting and payment system.",
    "jurisdiction": "Internal Policy",
    "jurisdiction_notes": "Daily reconciliation required for financial accuracy",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Price Label Accuracy Audit",
    "category": "Consumer Protection",
    "frequency": "P14D",
    "lead_times_days": [3],
    "owner_role": "Floor Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Price Accuracy Log" }
    ],
    "pass_fail_criteria": "Ensure product pricing displayed on shelf, signage, and system matches checkout price.",
    "jurisdiction": "Consumer Protection",
    "jurisdiction_notes": "Compliance with pricing accuracy standards and consumer protection laws",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Vendor & Purchase Order Compliance",
    "category": "Procurement",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Procurement Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Vendor Agreements" },
      { "type": "pdf", "label": "PO Records" }
    ],
    "pass_fail_criteria": "Confirm vendors have valid business licenses and contracts match pricing and terms.",
    "jurisdiction": "Business Licensing",
    "jurisdiction_notes": "Verify vendor compliance with business registration and licensing requirements",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Inventory Shrinkage and Loss Prevention Review",
    "category": "Asset Protection",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Store Manager / Security",
    "evidence_types": [
      { "type": "pdf", "label": "Inventory Reports" },
      { "type": "pdf", "label": "Incident Records" }
    ],
    "pass_fail_criteria": "Identify causes of product loss including damaged goods, theft, and stock errors.",
    "jurisdiction": "Internal Policy",
    "jurisdiction_notes": "Monthly review to identify and address inventory shrinkage causes",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Food & Perishables Storage Compliance (If Applicable)",
    "category": "Food Safety",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Department Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Temperature Logs" },
      { "type": "pdf", "label": "Waste Records" }
    ],
    "pass_fail_criteria": "Ensure temperature logs, product handling, and storage areas meet food safety standards.",
    "jurisdiction": "Health Department",
    "jurisdiction_notes": "Comply with local health department food safety regulations",
    "risk_level": "High"
  }
];

// Default templates for "Transportation and Logistics" industry
const TRANSPORTATION_LOGISTICS_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Fleet Vehicle Preventive Maintenance Check",
    "category": "Maintenance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Fleet Maintenance Technician",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Checklist" },
      { "type": "pdf", "label": "Maintenance Records" }
    ],
    "pass_fail_criteria": "Vehicle fluids, brakes, tires, lights, mileage logs, and mechanical inspection.",
    "jurisdiction": "DOT",
    "jurisdiction_notes": "DOT requires regular vehicle maintenance to ensure road safety compliance",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Driver CDL & Safety Certification Verification",
    "category": "Regulatory Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Driver License Copy" },
      { "type": "pdf", "label": "Training Certificates" }
    ],
    "pass_fail_criteria": "Ensure driver credentials, CDL, medical certificates, and training are up to date.",
    "jurisdiction": "DOT/FMCSA",
    "jurisdiction_notes": "FMCSA requires valid CDL and medical certification for commercial drivers",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "DOT / FMCSA Hours of Service Log Review",
    "category": "Regulatory Compliance",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Dispatch Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Electronic Logbook Records" }
    ],
    "pass_fail_criteria": "Review driver logs to ensure compliance with maximum driving time and rest break rules.",
    "jurisdiction": "DOT/FMCSA",
    "jurisdiction_notes": "FMCSA Hours of Service regulations require accurate logbook maintenance",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Cargo Loading and Securement Safety Check",
    "category": "Safety",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Driver / Load Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Load Sheet" },
      { "type": "pdf", "label": "Securement Checklist" }
    ],
    "pass_fail_criteria": "Verify that cargo is secured and weight distribution follows legal and safety guidelines.",
    "jurisdiction": "DOT",
    "jurisdiction_notes": "DOT cargo securement rules must be followed for each load",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Warehouse Equipment Safety Inspection",
    "category": "Safety",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Warehouse Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Equipment Inspection Log" }
    ],
    "pass_fail_criteria": "Inspect forklifts, pallet jacks, conveyors, and dock equipment for damage and safety function.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA requires regular inspection of powered industrial trucks and warehouse equipment",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hazmat Transportation Documentation (If Applicable)",
    "category": "Regulatory Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Hazmat Shipping Papers" },
      { "type": "pdf", "label": "Training Proof" }
    ],
    "pass_fail_criteria": "Ensure manifest, placards, emergency procedures, and packaging meet hazmat regulations.",
    "jurisdiction": "DOT/PHMSA",
    "jurisdiction_notes": "PHMSA hazmat transportation regulations require proper documentation and training",
    "risk_level": "High"
  }
];

// Default templates for "Education" industry
const EDUCATION_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Campus Safety & Emergency Preparedness Drill",
    "category": "Life Safety",
    "frequency": "P3M",
    "lead_times_days": [14],
    "owner_role": "Safety Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Logs" },
      { "type": "pdf", "label": "Evacuation Maps" }
    ],
    "pass_fail_criteria": "Conduct fire, lockdown, evacuation, and safety drills with staff and students.",
    "jurisdiction": "State Education Department",
    "jurisdiction_notes": "State education departments typically require regular safety drills for schools",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Student Privacy (FERPA) Compliance Review",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Administrator / Data Protection Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Access Logs" },
      { "type": "pdf", "label": "Policy Acknowledgements" }
    ],
    "pass_fail_criteria": "Ensure student data access, record handling, and digital systems protect privacy.",
    "jurisdiction": "FERPA",
    "jurisdiction_notes": "Family Educational Rights and Privacy Act (FERPA) requires annual compliance review",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Curriculum & Accreditation Documentation Check",
    "category": "Academic Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60],
    "owner_role": "Academic Dean",
    "evidence_types": [
      { "type": "pdf", "label": "Curriculum Documents" },
      { "type": "pdf", "label": "Accreditation Reports" }
    ],
    "pass_fail_criteria": "Verify teaching materials, course outlines, and assessments meet accreditation standards.",
    "jurisdiction": "Accrediting Body",
    "jurisdiction_notes": "Accreditation bodies require annual documentation review for continued accreditation",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Classroom Safety & Hazard Inspection",
    "category": "Safety",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Facilities / Principal",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Checklist" },
      { "type": "pdf", "label": "Corrective Action Log" }
    ],
    "pass_fail_criteria": "Ensure furniture, lab areas, electrical outlets, and emergency equipment are safe.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA requires regular safety inspections of educational facilities",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Employee Background & Credential Verification",
    "category": "HR Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "HR / District Administrator",
    "evidence_types": [
      { "type": "pdf", "label": "License Copies" },
      { "type": "pdf", "label": "Screening Records" }
    ],
    "pass_fail_criteria": "Confirm teacher certifications, license renewals, and background screening compliance.",
    "jurisdiction": "State Education Department",
    "jurisdiction_notes": "State education departments require verification of teacher credentials and background checks",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Food Service Safety & Sanitation (If Cafeteria Present)",
    "category": "Food Safety",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Food Service Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Temperature Logs" },
      { "type": "pdf", "label": "Sanitation Checklist" }
    ],
    "pass_fail_criteria": "Inspect food storage, prep, temperature logs, and hygiene compliance.",
    "jurisdiction": "Health Department",
    "jurisdiction_notes": "Local health departments require daily food safety inspections for school cafeterias",
    "risk_level": "High"
  }
];

// Default templates for "Professional, Scientific, and Technical Services" industry
const PROFESSIONAL_SCIENTIFIC_TECHNICAL_SERVICES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Client Contract and Engagement Review",
    "category": "Legal Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Project Manager / Legal",
    "evidence_types": [
      { "type": "pdf", "label": "Engagement Letter" },
      { "type": "pdf", "label": "NDA" },
      { "type": "pdf", "label": "Contract" }
    ],
    "pass_fail_criteria": "Ensure scope of work, deliverables, confidentiality terms, and billing terms are documented and signed.",
    "jurisdiction": "Contract Law",
    "jurisdiction_notes": "Proper contract documentation required for all client engagements",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Intellectual Property & Data Confidentiality Compliance",
    "category": "Data Protection",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Compliance Officer / IT",
    "evidence_types": [
      { "type": "pdf", "label": "Access Logs" },
      { "type": "pdf", "label": "Data Handling Policy" }
    ],
    "pass_fail_criteria": "Verify access controls, storage, and sharing of research, documents, and data respect confidentiality requirements.",
    "jurisdiction": "Data Protection Regulations",
    "jurisdiction_notes": "Compliance with client confidentiality agreements and data protection regulations",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Professional Licensing & Certification Verification",
    "category": "Regulatory Compliance",
    "frequency": "P6M",
    "lead_times_days": [30],
    "owner_role": "HR / Office Manager",
    "evidence_types": [
      { "type": "pdf", "label": "License Copies" },
      { "type": "pdf", "label": "Renewal Records" }
    ],
    "pass_fail_criteria": "Ensure employees in regulated professions maintain current certificates, licenses, and continuing education.",
    "jurisdiction": "Professional Licensing Boards",
    "jurisdiction_notes": "State and professional licensing boards require current credentials for regulated professions",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Project Deliverable Quality Assurance Review",
    "category": "Quality Assurance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Senior Consultant / Lead Scientist",
    "evidence_types": [
      { "type": "pdf", "label": "QA Checklist" },
      { "type": "pdf", "label": "Deliverable Drafts" },
      { "type": "pdf", "label": "Final Approval Sign-Off" }
    ],
    "pass_fail_criteria": "Perform peer review and QA verification before delivery of reports, research, or recommendations.",
    "jurisdiction": "Internal Policy",
    "jurisdiction_notes": "Quality assurance required for all client deliverables per company standards",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Client Data Retention & Destruction Compliance",
    "category": "Records Management",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Records Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Retention Schedule" },
      { "type": "pdf", "label": "Destruction Log" }
    ],
    "pass_fail_criteria": "Ensure records are retained for the required duration and securely disposed afterwards.",
    "jurisdiction": "Legal/Regulatory Requirements",
    "jurisdiction_notes": "Compliance with legal retention requirements and secure data destruction protocols",
    "risk_level": "Medium"
  }
];

// Default templates for "Energy and Utilities" industry
const ENERGY_UTILITIES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Safety and Hazard Communication Training (HAZCOM)",
    "category": "Safety Training",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Training Certificates" },
      { "type": "pdf", "label": "Safety Program Manual" }
    ],
    "pass_fail_criteria": "Ensure staff handling electrical, fuel, gas, or hazardous systems receive required safety training.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA Hazard Communication Standard (29 CFR 1910.1200) requires annual HAZCOM training",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Equipment Preventive Maintenance & Calibration",
    "category": "Maintenance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Maintenance Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Maintenance Logs" },
      { "type": "pdf", "label": "Calibration Certificates" }
    ],
    "pass_fail_criteria": "Inspect and service critical infrastructure equipment to prevent breakdowns and ensure safe operation.",
    "jurisdiction": "Industry Standards",
    "jurisdiction_notes": "Regular maintenance required per manufacturer specifications and industry best practices",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Regulatory Reporting Submission Review",
    "category": "Regulatory Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Regulatory Compliance Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Report Copies" },
      { "type": "pdf", "label": "Audit Records" }
    ],
    "pass_fail_criteria": "Verify that environmental, utility, usage, outage, or compliance reports are filed accurately and on time.",
    "jurisdiction": "EPA/FERC/State Regulatory",
    "jurisdiction_notes": "Environmental Protection Agency, Federal Energy Regulatory Commission, and state agencies require quarterly reporting",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Response and Outage Preparedness Drill",
    "category": "Emergency Preparedness",
    "frequency": "P6M",
    "lead_times_days": [14],
    "owner_role": "Operations Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Logs" },
      { "type": "pdf", "label": "Response Plan" }
    ],
    "pass_fail_criteria": "Conduct blackout, spill, leak, or system failure response simulations.",
    "jurisdiction": "NERC/FERC",
    "jurisdiction_notes": "North American Electric Reliability Corporation and FERC require regular emergency response drills",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Permit and Operating License Renewal Check",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Permit Copies" },
      { "type": "pdf", "label": "Expiration Tracker" }
    ],
    "pass_fail_criteria": "Ensure all plant, utility, or energy distribution permits remain active and updated.",
    "jurisdiction": "State/Federal Regulatory",
    "jurisdiction_notes": "State public utility commissions and federal agencies require active permits for operation",
    "risk_level": "High"
  }
];

// Default templates for "Environmental Services" industry
const ENVIRONMENTAL_SERVICES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Environmental Impact Assessment Documentation",
    "category": "Environmental Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Environmental Engineer / Compliance Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Impact Statement" },
      { "type": "pdf", "label": "Approval Forms" }
    ],
    "pass_fail_criteria": "Review environmental effects of projects, remediation, disposal, and restoration activities.",
    "jurisdiction": "EPA/NEPA",
    "jurisdiction_notes": "National Environmental Policy Act (NEPA) and EPA regulations require environmental impact assessments",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Hazardous Waste Handling & Disposal Compliance",
    "category": "Waste Management",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Waste Management Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Manifests" },
      { "type": "pdf", "label": "Disposal Receipts" },
      { "type": "pdf", "label": "Storage Logs" }
    ],
    "pass_fail_criteria": "Ensure waste is classified, labeled, stored, and transported per local and federal regulations.",
    "jurisdiction": "EPA/RCRA",
    "jurisdiction_notes": "Resource Conservation and Recovery Act (RCRA) requires proper hazardous waste management",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Field Equipment Decontamination and Calibration Check",
    "category": "Quality Assurance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Field Technician",
    "evidence_types": [
      { "type": "pdf", "label": "Calibration Logs" },
      { "type": "pdf", "label": "Decontamination Records" }
    ],
    "pass_fail_criteria": "Clean and calibrate sampling kits, meters, and testing instruments to maintain accuracy.",
    "jurisdiction": "Industry Standards",
    "jurisdiction_notes": "EPA and ASTM standards require proper calibration and decontamination of field equipment",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Air, Water, and Soil Sampling QA/QC Procedure Review",
    "category": "Quality Assurance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Lab Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Chain of Custody Forms" },
      { "type": "pdf", "label": "Lab Certifications" }
    ],
    "pass_fail_criteria": "Verify chain-of-custody records, lab certifications, and sampling conditions.",
    "jurisdiction": "EPA/ISO",
    "jurisdiction_notes": "EPA and ISO 17025 require proper chain of custody and certified laboratory procedures",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Environmental Compliance Report and Audit Filing",
    "category": "Regulatory Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Regulatory Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Environmental Report" },
      { "type": "pdf", "label": "Audit Checklist" }
    ],
    "pass_fail_criteria": "Submit monitoring reports and maintain audit readiness for EPA/state inspections.",
    "jurisdiction": "EPA/State Regulatory",
    "jurisdiction_notes": "EPA and state environmental agencies require quarterly compliance reporting",
    "risk_level": "High"
  }
];

// Default templates for "Finance and Insurance" industry
const FINANCE_INSURANCE_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Customer Identity Verification (KYC)",
    "category": "Regulatory Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Compliance / Account Manager",
    "evidence_types": [
      { "type": "pdf", "label": "ID Documents" },
      { "type": "pdf", "label": "Proof of Address" },
      { "type": "pdf", "label": "KYC Form" }
    ],
    "pass_fail_criteria": "Verify customer identity documents, address, financial history, and risk profile before onboarding.",
    "jurisdiction": "FinCEN/Bank Secrecy Act",
    "jurisdiction_notes": "Bank Secrecy Act and FinCEN require Know Your Customer (KYC) verification for all new accounts",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Anti-Money Laundering (AML) Transaction Monitoring",
    "category": "Regulatory Compliance",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Transaction Reports" },
      { "type": "pdf", "label": "SAR Filing Records" }
    ],
    "pass_fail_criteria": "Review financial transactions for suspicious activity and file SARs when applicable.",
    "jurisdiction": "FinCEN/Bank Secrecy Act",
    "jurisdiction_notes": "Bank Secrecy Act requires daily transaction monitoring and Suspicious Activity Report (SAR) filing",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Financial Statement Accuracy and Audit Review",
    "category": "Financial Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Finance Manager / Auditor",
    "evidence_types": [
      { "type": "pdf", "label": "Reconciliation Reports" },
      { "type": "pdf", "label": "General Ledger" },
      { "type": "pdf", "label": "Audit Checklist" }
    ],
    "pass_fail_criteria": "Verify ledger balances, account reconciliations, and ensure audit readiness.",
    "jurisdiction": "GAAP/SEC",
    "jurisdiction_notes": "Generally Accepted Accounting Principles (GAAP) and SEC regulations require monthly reconciliation",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Insurance Policy Compliance and Coverage Renewal",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60],
    "owner_role": "Insurance Underwriter / Broker",
    "evidence_types": [
      { "type": "pdf", "label": "Policy Documents" },
      { "type": "pdf", "label": "Disclosure Forms" }
    ],
    "pass_fail_criteria": "Confirm client policy terms, benefits, exclusions, renewals, and regulatory disclosures.",
    "jurisdiction": "State Insurance Department",
    "jurisdiction_notes": "State insurance departments require annual policy review and renewal compliance",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Investment Suitability and Risk Profile Review",
    "category": "Regulatory Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Wealth Advisor / Portfolio Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Risk Assessment Form" },
      { "type": "pdf", "label": "Portfolio Review Report" }
    ],
    "pass_fail_criteria": "Ensure client investment choices align with risk tolerance and compliance regulations.",
    "jurisdiction": "SEC/FINRA",
    "jurisdiction_notes": "SEC and FINRA require suitability assessments for all investment recommendations",
    "risk_level": "High"
  },
  {
    "name": "Branch Operational Compliance Review",
    "category": "Operational Compliance",
    "frequency": "P1M",
    "lead_times_days": [7, 3],
    "owner_role": "Branch Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Branch Compliance Checklist", "required": true },
      { "type": "photo", "label": "Branch Operations Evidence", "required": false }
    ],
    "pass_fail_criteria": "All required operational controls reviewed, exceptions documented, and corrective actions assigned.",
    "jurisdiction": "State Banking Department",
    "jurisdiction_notes": "Branch-level operational compliance required for routine regulatory examinations and internal audits.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Cash Handling and Vault Control Audit",
    "category": "Operational Compliance",
    "frequency": "P1M",
    "lead_times_days": [7, 2],
    "owner_role": "Branch Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Cash Reconciliation Report", "required": true },
      { "type": "pdf", "label": "Vault Access Log", "required": true }
    ],
    "pass_fail_criteria": "Cash balances reconciled, dual control enforced, and vault access properly logged.",
    "jurisdiction": "State Banking Department",
    "jurisdiction_notes": "Cash controls are reviewed during safety and soundness examinations.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Information Security Controls Assessment",
    "category": "Security",
    "frequency": "P3M",
    "lead_times_days": [14, 7],
    "owner_role": "IT Security Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Information Security Assessment Report", "required": true },
      { "type": "pdf", "label": "Access Control Review", "required": true }
    ],
    "pass_fail_criteria": "User access reviewed, security controls validated, and identified risks documented with remediation plans.",
    "jurisdiction": "FFIEC",
    "jurisdiction_notes": "Aligned with FFIEC IT Examination Handbook requirements.",
    "risk_level": "High",
    "mark_due_in_days": 45
  },
  {
    "name": "User Access and Privileged Access Review",
    "category": "Security",
    "frequency": "P3M",
    "lead_times_days": [10, 5],
    "owner_role": "IT Administrator",
    "evidence_types": [
      { "type": "pdf", "label": "User Access Listing", "required": true },
      { "type": "pdf", "label": "Privileged Access Approval Records", "required": true }
    ],
    "pass_fail_criteria": "All user and privileged access reviewed, unauthorized access removed, and approvals documented.",
    "jurisdiction": "FFIEC",
    "jurisdiction_notes": "Required for logical access control reviews under FFIEC and internal audit standards.",
    "risk_level": "High",
    "mark_due_in_days": 45
  },
  {
    "name": "Customer Data Privacy and Handling Review",
    "category": "Customer Protection",
    "frequency": "P3M",
    "lead_times_days": [14, 7],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Privacy Compliance Checklist", "required": true },
      { "type": "photo", "label": "Secure Storage Evidence", "required": false }
    ],
    "pass_fail_criteria": "Customer data handled according to privacy policies, with no unsecured exposure of PII.",
    "jurisdiction": "GLBA",
    "jurisdiction_notes": "Supports compliance with Gramm-Leach-Bliley Act Safeguards Rule.",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Incident Reporting and Escalation Readiness",
    "category": "Risk Management",
    "frequency": "P3M",
    "lead_times_days": [10, 5],
    "owner_role": "Risk Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Incident Response Procedure", "required": true },
      { "type": "pdf", "label": "Incident Log Review", "required": false }
    ],
    "pass_fail_criteria": "Incident reporting procedures reviewed and staff escalation readiness confirmed.",
    "jurisdiction": "FDIC",
    "jurisdiction_notes": "Incident preparedness evaluated during risk management and safety examinations.",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Compliance Training and Policy Acknowledgement Review",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60, 30, 7],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Training Completion Records", "required": true },
      { "type": "pdf", "label": "Policy Acknowledgement Forms", "required": true }
    ],
    "pass_fail_criteria": "All required staff training completed and policies formally acknowledged.",
    "jurisdiction": "OCC",
    "jurisdiction_notes": "Employee training and awareness required for regulatory compliance programs.",
    "risk_level": "Medium",
    "mark_due_in_days": 90
  },
  {
    "name": "Third-Party Vendor Compliance Review",
    "category": "Risk Management",
    "frequency": "P1Y",
    "lead_times_days": [60, 30],
    "owner_role": "Vendor Risk Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Vendor Risk Assessment", "required": true },
      { "type": "pdf", "label": "Vendor Contract and SLA", "required": false }
    ],
    "pass_fail_criteria": "Vendor risks assessed, critical vendors reviewed, and mitigation actions documented.",
    "jurisdiction": "OCC",
    "jurisdiction_notes": "Third-party risk management required under OCC and FFIEC guidance.",
    "risk_level": "High",
    "mark_due_in_days": 60
  },
  {
    "name": "Business Continuity and Disaster Recovery Readiness",
    "category": "Operational Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60, 30],
    "owner_role": "Operations Manager",
    "evidence_types": [
      { "type": "pdf", "label": "BCP/DR Plan", "required": true },
      { "type": "pdf", "label": "Test or Drill Results", "required": false }
    ],
    "pass_fail_criteria": "Business continuity plans reviewed and recovery readiness validated.",
    "jurisdiction": "FFIEC",
    "jurisdiction_notes": "Required as part of operational resilience and continuity planning.",
    "risk_level": "High",
    "mark_due_in_days": 90
  },
  {
    "name": "Physical Security and Access Controls Review",
    "category": "Security",
    "frequency": "P3M",
    "lead_times_days": [14, 7],
    "owner_role": "Facilities Manager",
    "evidence_types": [
      { "type": "photo", "label": "Access Control Evidence", "required": true },
      { "type": "pdf", "label": "Physical Access Log", "required": false }
    ],
    "pass_fail_criteria": "Physical access controls reviewed and unauthorized access risks mitigated.",
    "jurisdiction": "State Banking Department",
    "jurisdiction_notes": "Physical security reviewed during branch examinations.",
    "risk_level": "Medium",
    "mark_due_in_days": 45
  }
];

// Default templates for "Information Technology and Telecommunications" industry
const INFORMATION_TECHNOLOGY_TELECOMMUNICATIONS_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "System Access Control & User Permission Audit",
    "category": "Information Security",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "IT Security Administrator",
    "evidence_types": [
      { "type": "pdf", "label": "User Access List" },
      { "type": "pdf", "label": "Permission Logs" }
    ],
    "pass_fail_criteria": "Ensure only authorized staff have system access, with proper role-based permission levels.",
    "jurisdiction": "SOC 2/ISO 27001",
    "jurisdiction_notes": "SOC 2 and ISO 27001 require regular access control audits",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Data Backup and Recovery Drill",
    "category": "Business Continuity",
    "frequency": "P3M",
    "lead_times_days": [14],
    "owner_role": "IT Operations Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Backup Logs" },
      { "type": "pdf", "label": "Recovery Test Results" }
    ],
    "pass_fail_criteria": "Test backup restore capabilities for critical systems to ensure data resilience.",
    "jurisdiction": "Business Continuity Standards",
    "jurisdiction_notes": "Quarterly backup and recovery testing required for business continuity compliance",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Network Security Patch and Vulnerability Review",
    "category": "Information Security",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Cybersecurity Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Patch Logs" },
      { "type": "pdf", "label": "Vulnerability Reports" }
    ],
    "pass_fail_criteria": "Update software, review firewall rules, and scan for vulnerabilities.",
    "jurisdiction": "NIST/CIS",
    "jurisdiction_notes": "NIST Cybersecurity Framework and CIS Controls require monthly vulnerability assessments",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Incident Response and Breach Log Documentation",
    "category": "Information Security",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Security Response Team",
    "evidence_types": [
      { "type": "pdf", "label": "Incident Logs" },
      { "type": "pdf", "label": "Corrective Action Record" }
    ],
    "pass_fail_criteria": "Document and respond to security threats, outages, or data breaches.",
    "jurisdiction": "GDPR/HIPAA/State Breach Laws",
    "jurisdiction_notes": "GDPR, HIPAA, and state breach notification laws require incident documentation and reporting",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Telecommunications Infrastructure Performance Check",
    "category": "Operations",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Network Engineer",
    "evidence_types": [
      { "type": "pdf", "label": "Uptime Report" },
      { "type": "pdf", "label": "NOC Logs" }
    ],
    "pass_fail_criteria": "Monitor uptime metrics, latency, bandwidth usage, and SLA compliance.",
    "jurisdiction": "SLA Compliance",
    "jurisdiction_notes": "Service Level Agreements require weekly performance monitoring and reporting",
    "risk_level": "Medium"
  }
];

// Default templates for "Arts, Entertainment, and Recreation" industry
const ARTS_ENTERTAINMENT_RECREATION_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Event Safety & Crowd Management Plan Review",
    "category": "Safety",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Event or Venue Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Safety Plan" },
      { "type": "pdf", "label": "Staffing Roster" }
    ],
    "pass_fail_criteria": "Verify emergency exits, crowd flow, security staffing, and communication procedures.",
    "jurisdiction": "Local Fire Marshal",
    "jurisdiction_notes": "Local fire marshal and venue safety regulations require event safety plans",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Equipment & Facility Maintenance Inspection",
    "category": "Safety",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Facilities Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Checklist" },
      { "type": "pdf", "label": "Repair Logs" }
    ],
    "pass_fail_criteria": "Inspect lighting, sound systems, seating, playgrounds, sports gear, or stage equipment.",
    "jurisdiction": "OSHA/Industry Standards",
    "jurisdiction_notes": "OSHA and industry safety standards require regular equipment inspections",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Performer / Staff Background and Credential Verification",
    "category": "HR Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "HR / Program Director",
    "evidence_types": [
      { "type": "pdf", "label": "Certification Copies" },
      { "type": "pdf", "label": "Background Screening Records" }
    ],
    "pass_fail_criteria": "Ensure instructors, coaches, performers, or guides meet licensing and safety qualifications.",
    "jurisdiction": "State Licensing",
    "jurisdiction_notes": "State and local regulations require verification of credentials for instructors and performers",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Ticketing and Attendance Revenue Audit",
    "category": "Financial Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Finance / Box Office Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Sales Reports" },
      { "type": "pdf", "label": "Deposit Receipts" }
    ],
    "pass_fail_criteria": "Verify that attendance counts match ticket sales and revenue deposits.",
    "jurisdiction": "Internal Policy",
    "jurisdiction_notes": "Financial accuracy and reconciliation required for each event",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Health, Sanitation, and Food & Drinks Compliance (If applicable)",
    "category": "Food Safety",
    "frequency": "P1D",
    "lead_times_days": [0],
    "owner_role": "Food Service Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Sanitation Checklist" },
      { "type": "pdf", "label": "Food Handler Permits" }
    ],
    "pass_fail_criteria": "Ensure concession stands and food services maintain hygiene and licensing standards.",
    "jurisdiction": "Health Department",
    "jurisdiction_notes": "Local health departments require daily sanitation checks and valid food handler permits",
    "risk_level": "High"
  }
];

// Default templates for "Public Administration and Government" industry
const PUBLIC_ADMINISTRATION_GOVERNMENT_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Procurement and Contract Compliance Review",
    "category": "Regulatory Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Procurement Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Contract Files" },
      { "type": "pdf", "label": "Bid Records" },
      { "type": "pdf", "label": "Vendor Screening Records" }
    ],
    "pass_fail_criteria": "Ensure government procurement guidelines are followed, and contracts are properly approved and tracked.",
    "jurisdiction": "Federal/State Procurement",
    "jurisdiction_notes": "Federal Acquisition Regulation (FAR) and state procurement laws require proper contract documentation",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Public Records and FOIA Compliance",
    "category": "Regulatory Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Records Administrator",
    "evidence_types": [
      { "type": "pdf", "label": "Records Retention Log" },
      { "type": "pdf", "label": "FOIA Request Log" }
    ],
    "pass_fail_criteria": "Ensure retention, access, and disclosure handling meets public record and FOIA requirements.",
    "jurisdiction": "FOIA/State Public Records",
    "jurisdiction_notes": "Freedom of Information Act (FOIA) and state public records laws require quarterly compliance review",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Budget and Expenditure Transparency Audit",
    "category": "Financial Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Finance Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Budget Reports" },
      { "type": "pdf", "label": "Expenditure Logs" }
    ],
    "pass_fail_criteria": "Review spending to ensure compliance with budget authorization and public reporting standards.",
    "jurisdiction": "Government Accounting Standards",
    "jurisdiction_notes": "Government Accounting Standards Board (GASB) requires monthly budget and expenditure reporting",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Employee Ethics and Conflict of Interest Disclosure",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR / Legal",
    "evidence_types": [
      { "type": "pdf", "label": "Ethics Disclosure Forms" }
    ],
    "pass_fail_criteria": "Verify annual ethics forms, political activity restrictions, and conflict-of-interest updates.",
    "jurisdiction": "Federal/State Ethics Laws",
    "jurisdiction_notes": "Federal and state ethics laws require annual disclosure of conflicts of interest and political activities",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Preparedness and Continuity Plan Drill",
    "category": "Emergency Preparedness",
    "frequency": "P6M",
    "lead_times_days": [14],
    "owner_role": "Emergency Response Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Logs" },
      { "type": "pdf", "label": "Continuity Plans" }
    ],
    "pass_fail_criteria": "Conduct drills to ensure public agencies can maintain critical services during disruptions.",
    "jurisdiction": "FEMA/State Emergency Management",
    "jurisdiction_notes": "FEMA and state emergency management agencies require semi-annual continuity plan drills",
    "risk_level": "High"
  }
];

// Default templates for "Nonprofit Organizations" industry
const NONPROFIT_ORGANIZATIONS_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Donor Fund Allocation and Use Compliance",
    "category": "Financial Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Finance Manager / Board Treasurer",
    "evidence_types": [
      { "type": "pdf", "label": "Grant Letters" },
      { "type": "pdf", "label": "Fund Allocation Reports" }
    ],
    "pass_fail_criteria": "Verify funds are used according to donor intent and nonprofit mission requirements.",
    "jurisdiction": "IRS/Donor Requirements",
    "jurisdiction_notes": "IRS and donor requirements mandate proper fund allocation and reporting",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Volunteer Background Screening & Training",
    "category": "HR Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Volunteer Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Background Checks" },
      { "type": "pdf", "label": "Training Certificates" }
    ],
    "pass_fail_criteria": "Confirm volunteers are vetted and trained according to program requirements.",
    "jurisdiction": "State/Local Regulations",
    "jurisdiction_notes": "State and local regulations require background screening for volunteers working with vulnerable populations",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "501(c)3 Tax-Exempt Status Compliance Review",
    "category": "Regulatory Compliance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Executive Director",
    "evidence_types": [
      { "type": "pdf", "label": "IRS Form 990" },
      { "type": "pdf", "label": "Mission Statement Alignment Report" }
    ],
    "pass_fail_criteria": "Ensure organization activities align with nonprofit tax-exempt operational rules.",
    "jurisdiction": "IRS",
    "jurisdiction_notes": "IRS requires annual Form 990 filing and compliance with 501(c)3 operational requirements",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Program Outcome & Impact Reporting",
    "category": "Program Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Program Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Program Metrics" },
      { "type": "pdf", "label": "Impact Evaluation Report" }
    ],
    "pass_fail_criteria": "Measure and document outcomes for funded activities and public reporting.",
    "jurisdiction": "Grant Requirements",
    "jurisdiction_notes": "Grantors and funders require quarterly outcome and impact reporting",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Board Governance and Policy Review",
    "category": "Governance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Board Chair / Governance Committee",
    "evidence_types": [
      { "type": "pdf", "label": "Bylaws" },
      { "type": "pdf", "label": "Meeting Minutes" },
      { "type": "pdf", "label": "Conflict Forms" }
    ],
    "pass_fail_criteria": "Ensure board decisions, bylaws, conflict disclosures, and meeting minutes are up to date.",
    "jurisdiction": "State Nonprofit Law",
    "jurisdiction_notes": "State nonprofit corporation laws require annual governance and policy review",
    "risk_level": "High"
  }
];

// Default templates for "Agriculture, Forestry, Fishing, and Hunting" industry
const AGRICULTURE_FORESTRY_FISHING_HUNTING_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Crop and Livestock Health & Biosecurity Check",
    "category": "Animal/Crop Health",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Farm / Ranch Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Health Inspection Logs" }
    ],
    "pass_fail_criteria": "Inspect herds, crops, feed, and pest control for disease prevention and environmental compliance.",
    "jurisdiction": "USDA/State Agriculture",
    "jurisdiction_notes": "USDA and state agriculture departments require regular health and biosecurity inspections",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Pesticide and Chemical Storage Compliance",
    "category": "Environmental Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Chemical Inventory" },
      { "type": "pdf", "label": "Safety Data Sheets" }
    ],
    "pass_fail_criteria": "Verify chemical storage, labeling, PPE availability, and usage logs.",
    "jurisdiction": "EPA",
    "jurisdiction_notes": "EPA requires proper pesticide and chemical storage per Worker Protection Standard",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Water Use and Irrigation Permit Compliance",
    "category": "Environmental Compliance",
    "frequency": "P1Y",
    "lead_times_days": [60],
    "owner_role": "Operations Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Permit Copies" },
      { "type": "pdf", "label": "Water Usage Log" }
    ],
    "pass_fail_criteria": "Ensure irrigation practices follow regulatory water rights and conservation guidelines.",
    "jurisdiction": "State Water Authority",
    "jurisdiction_notes": "State water authorities require annual permit renewal and usage reporting",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Equipment Operation and Field Safety Training",
    "category": "Safety Training",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Safety Trainer",
    "evidence_types": [
      { "type": "pdf", "label": "Training Attendance Records" }
    ],
    "pass_fail_criteria": "Train workers operating tractors, harvesters, chainsaws, or fishing vessels.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA requires annual safety training for agricultural and forestry equipment operators",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Environmental Soil, Water, or Wildlife Impact Review",
    "category": "Environmental Compliance",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Environmental Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Environmental Monitoring Reports" }
    ],
    "pass_fail_criteria": "Evaluate land impact, runoff management, wildlife habitat protection practices.",
    "jurisdiction": "EPA/USDA",
    "jurisdiction_notes": "EPA and USDA require quarterly environmental impact monitoring for agricultural operations",
    "risk_level": "Medium"
  }
];

// Default templates for "Mining, Quarrying, and Oil & Gas Extraction" industry
const MINING_QUARRYING_OIL_GAS_EXTRACTION_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Site Safety & Hazard Awareness Training",
    "category": "Safety Training",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Safety Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Training Certificates" }
    ],
    "pass_fail_criteria": "Train workers on blasting, drilling, confined spaces, and heavy equipment hazards.",
    "jurisdiction": "MSHA/OSHA",
    "jurisdiction_notes": "Mine Safety and Health Administration (MSHA) and OSHA require annual safety training",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Environmental Impact Monitoring (Air, Water, Dust)",
    "category": "Environmental Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Environmental Compliance Lead",
    "evidence_types": [
      { "type": "pdf", "label": "Monitoring Logs" },
      { "type": "pdf", "label": "Regulatory Reports" }
    ],
    "pass_fail_criteria": "Measure environmental effects and ensure compliance with regulatory limits.",
    "jurisdiction": "EPA/State Environmental",
    "jurisdiction_notes": "EPA and state environmental agencies require monthly environmental monitoring",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Explosive & Chemical Handling and Storage Compliance",
    "category": "Safety",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Operations Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Storage Logs" },
      { "type": "pdf", "label": "Inventory Forms" }
    ],
    "pass_fail_criteria": "Track and secure explosive materials and hazardous chemicals according to regulations.",
    "jurisdiction": "ATF/EPA",
    "jurisdiction_notes": "Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) and EPA require weekly explosive and chemical tracking",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Equipment and Well / Pit Integrity Inspection",
    "category": "Safety",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Maintenance Technician",
    "evidence_types": [
      { "type": "pdf", "label": "Inspection Logs" },
      { "type": "pdf", "label": "Repair Orders" }
    ],
    "pass_fail_criteria": "Inspect rigs, vehicles, belts, drills, conveyor systems, mine shafts, or wellheads.",
    "jurisdiction": "MSHA/OSHA",
    "jurisdiction_notes": "MSHA and OSHA require weekly equipment and infrastructure inspections",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Emergency Evacuation, Containment, and Spill Response Drill",
    "category": "Emergency Preparedness",
    "frequency": "P6M",
    "lead_times_days": [14],
    "owner_role": "Emergency Response Team",
    "evidence_types": [
      { "type": "pdf", "label": "Drill Logs" },
      { "type": "pdf", "label": "Response Plan" }
    ],
    "pass_fail_criteria": "Test emergency evacuation, containment barriers, ventilation alarms, and spill response.",
    "jurisdiction": "MSHA/OSHA",
    "jurisdiction_notes": "MSHA and OSHA require semi-annual emergency response drills for mining and extraction operations",
    "risk_level": "High"
  }
];

// Default templates for "Administrative and Support Services" industry
const ADMINISTRATIVE_SUPPORT_SERVICES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Employee Credential and HR File Compliance",
    "category": "HR Compliance",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "HR Manager",
    "evidence_types": [
      { "type": "pdf", "label": "HR Files" },
      { "type": "pdf", "label": "Training Certificates" }
    ],
    "pass_fail_criteria": "Ensure I-9, certifications, training, and performance reviews are updated.",
    "jurisdiction": "DHS/IRS",
    "jurisdiction_notes": "Department of Homeland Security requires I-9 verification and IRS requires proper employment documentation",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Client Contract Service-Level Tracking",
    "category": "Contract Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Account Manager",
    "evidence_types": [
      { "type": "pdf", "label": "SLA Report" },
      { "type": "pdf", "label": "Client Communication Log" }
    ],
    "pass_fail_criteria": "Verify contracted service deliverables meet timelines and SLA commitments.",
    "jurisdiction": "Contract Terms",
    "jurisdiction_notes": "Client contracts require monthly service level agreement tracking and reporting",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Workplace Safety & Ergonomics Review",
    "category": "Safety",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Safety Coordinator",
    "evidence_types": [
      { "type": "pdf", "label": "Safety Inspection Checklist" }
    ],
    "pass_fail_criteria": "Check workstation layout, lifting training, and ergonomic setup to prevent injuries.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA requires quarterly ergonomic assessments for office and administrative work environments",
    "risk_level": "Medium",
    "mark_due_in_days": 30
  },
  {
    "name": "Client Data Confidentiality and Storage Audit",
    "category": "Data Protection",
    "frequency": "P3M",
    "lead_times_days": [15],
    "owner_role": "Compliance Officer",
    "evidence_types": [
      { "type": "pdf", "label": "Access Logs" },
      { "type": "pdf", "label": "Retention Records" }
    ],
    "pass_fail_criteria": "Ensure proper handling of physical documents and digital files.",
    "jurisdiction": "Data Protection Regulations",
    "jurisdiction_notes": "Client confidentiality agreements and data protection regulations require quarterly audits",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Service Delivery Quality Review",
    "category": "Quality Assurance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Quality Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Quality Reports" },
      { "type": "pdf", "label": "Client Feedback Forms" }
    ],
    "pass_fail_criteria": "Evaluate client satisfaction and performance quality metrics.",
    "jurisdiction": "Internal Policy",
    "jurisdiction_notes": "Monthly quality review required for service delivery excellence and client retention",
    "risk_level": "Medium"
  }
];

// Default templates for "Waste Management and Remediation Services" industry
const WASTE_MANAGEMENT_REMEDIATION_SERVICES_TEMPLATES: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> = [
  {
    "name": "Hazardous Waste Storage and Labeling Check",
    "category": "Waste Management",
    "frequency": "P1W",
    "lead_times_days": [3],
    "owner_role": "Compliance Supervisor",
    "evidence_types": [
      { "type": "pdf", "label": "Labeling Log" },
      { "type": "pdf", "label": "Storage Records" }
    ],
    "pass_fail_criteria": "Verify containers are labeled, secured, separated, and documented.",
    "jurisdiction": "EPA/RCRA",
    "jurisdiction_notes": "Resource Conservation and Recovery Act (RCRA) requires weekly hazardous waste storage inspections",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Transport Manifest and Disposal Record Review",
    "category": "Regulatory Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Logistics Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Waste Manifest" },
      { "type": "pdf", "label": "Disposal Certificate" }
    ],
    "pass_fail_criteria": "Ensure waste manifests match disposal receipts and approved facilities.",
    "jurisdiction": "EPA/DOT",
    "jurisdiction_notes": "EPA and DOT require proper manifesting and tracking for each hazardous waste shipment",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Air, Soil, and Water Environmental Testing",
    "category": "Environmental Compliance",
    "frequency": "P1M",
    "lead_times_days": [7],
    "owner_role": "Environmental Scientist",
    "evidence_types": [
      { "type": "pdf", "label": "Test Reports" },
      { "type": "pdf", "label": "Chain of Custody Forms" }
    ],
    "pass_fail_criteria": "Monitor environmental impact around disposal or remediation sites.",
    "jurisdiction": "EPA/State Environmental",
    "jurisdiction_notes": "EPA and state environmental agencies require monthly environmental testing at waste management sites",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Personal Protective Equipment Fit & Safety Training",
    "category": "Safety Training",
    "frequency": "P1Y",
    "lead_times_days": [30],
    "owner_role": "Safety Trainer",
    "evidence_types": [
      { "type": "pdf", "label": "Fit Test Records" },
      { "type": "pdf", "label": "Training Certificates" }
    ],
    "pass_fail_criteria": "Ensure workers handling waste have properly fitted PPE and training.",
    "jurisdiction": "OSHA",
    "jurisdiction_notes": "OSHA requires annual respiratory protection fit testing and safety training for waste handlers",
    "risk_level": "High",
    "mark_due_in_days": 30
  },
  {
    "name": "Cleanup Project Completion Documentation",
    "category": "Project Compliance",
    "frequency": "P0D",
    "lead_times_days": [0],
    "owner_role": "Project Manager",
    "evidence_types": [
      { "type": "pdf", "label": "Cleanup Reports" },
      { "type": "pdf", "label": "Regulatory Approval Letters" }
    ],
    "pass_fail_criteria": "Record remediation actions, site clearance, regulatory inspection approvals.",
    "jurisdiction": "EPA/State Environmental",
    "jurisdiction_notes": "EPA and state environmental agencies require completion documentation for each remediation project",
    "risk_level": "High"
  }
];

// Map of industry types to their default templates
const INDUSTRY_TEMPLATES_MAP: Record<string, Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>>> = {
  "Accommodation and Food Services": ACCOMMODATION_FOOD_SERVICES_TEMPLATES,
  "Construction": CONSTRUCTION_TEMPLATES,
  "Healthcare and Social Assistance": HEALTHCARE_SOCIAL_ASSISTANCE_TEMPLATES,
  "Manufacturing": MANUFACTURING_TEMPLATES,
  "Retail and Wholesale Trade": RETAIL_WHOLESALE_TRADE_TEMPLATES,
  "Transportation and Logistics": TRANSPORTATION_LOGISTICS_TEMPLATES,
  "Education": EDUCATION_TEMPLATES,
  "Professional, Scientific, and Technical Services": PROFESSIONAL_SCIENTIFIC_TECHNICAL_SERVICES_TEMPLATES,
  "Energy and Utilities": ENERGY_UTILITIES_TEMPLATES,
  "Environmental Services": ENVIRONMENTAL_SERVICES_TEMPLATES,
  "Finance and Insurance": FINANCE_INSURANCE_TEMPLATES,
  "Information Technology and Telecommunications": INFORMATION_TECHNOLOGY_TELECOMMUNICATIONS_TEMPLATES,
  "Arts, Entertainment, and Recreation": ARTS_ENTERTAINMENT_RECREATION_TEMPLATES,
  "Public Administration and Government": PUBLIC_ADMINISTRATION_GOVERNMENT_TEMPLATES,
  "Nonprofit Organizations": NONPROFIT_ORGANIZATIONS_TEMPLATES,
  "Agriculture, Forestry, Fishing, and Hunting": AGRICULTURE_FORESTRY_FISHING_HUNTING_TEMPLATES,
  "Mining, Quarrying, and Oil & Gas Extraction": MINING_QUARRYING_OIL_GAS_EXTRACTION_TEMPLATES,
  "Administrative and Support Services": ADMINISTRATIVE_SUPPORT_SERVICES_TEMPLATES,
  "Waste Management and Remediation Services": WASTE_MANAGEMENT_REMEDIATION_SERVICES_TEMPLATES,
  // Additional industries will be added here as templates are provided
  // "Real Estate and Property Management": REAL_ESTATE_TEMPLATES,
  // etc.
};

/**
 * Get default templates for a specific industry
 */
export function getDefaultTemplatesForIndustry(industryType: string): Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>> {
  const normalizedIndustry = industryType || DEFAULT_INDUSTRY_TYPE;
  return INDUSTRY_TEMPLATES_MAP[normalizedIndustry] || ACCOMMODATION_FOOD_SERVICES_TEMPLATES;
}

/**
 * Check if default templates have already been loaded for an organization
 */
export async function hasDefaultTemplatesLoaded(organizationId: string, industryType: string): Promise<boolean> {
  try {
    const templatesResult = await complianceService.getAllTemplates(organizationId);
    if (!templatesResult.success || !templatesResult.data) {
      return false;
    }

    const defaultTemplates = getDefaultTemplatesForIndustry(industryType);
    const defaultTemplateNames = new Set(defaultTemplates.map(t => t.name));

    // Check if any of the default templates exist
    return templatesResult.data.some(template => defaultTemplateNames.has(template.name));
  } catch (error) {
    console.error('Error checking for default templates:', error);
    return false;
  }
}

/**
 * Load default templates for an organization based on its industry type
 */
export async function loadDefaultTemplatesForOrganization(organization: Organization): Promise<{ success: boolean; error?: string; count?: number }> {
  if (!organization.id) {
    return { success: false, error: 'Organization ID is required' };
  }

  const industryType = getIndustryType(organization);
  const defaultTemplates = getDefaultTemplatesForIndustry(industryType);

  // Check if templates are already loaded
  const alreadyLoaded = await hasDefaultTemplatesLoaded(organization.id, industryType);
  if (alreadyLoaded) {
    return { success: false, error: 'Default templates have already been loaded for this organization' };
  }

  // Load templates one by one
  let successCount = 0;
  let errorCount = 0;

  for (const template of defaultTemplates) {
    try {
      const result = await complianceService.addTemplate(template, organization.id);
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to load template "${template.name}":`, result.error);
      }
    } catch (error: any) {
      errorCount++;
      console.error(`Error loading template "${template.name}":`, error);
    }
  }

  if (successCount > 0) {
    return { success: true, count: successCount };
  } else {
    return { success: false, error: `Failed to load any templates. ${errorCount} errors occurred.` };
  }
}

/**
 * Add templates for a new industry type
 * This function will be used when templates for other industries are provided
 */
export function addIndustryTemplates(industry: string, templates: Array<Omit<ComplianceTemplate, 'id' | 'organizationId' | 'created_at' | 'updated_at'>>): void {
  INDUSTRY_TEMPLATES_MAP[industry] = templates;
}







